import { getSnapshot, isStateTreeNode } from 'mobx-state-tree';

import ProductClass from '../types/ProductClass';

export const WISHLIST_STORAGE_VERSION = 2;

const itemMetaDefaults = {
  dirty: false,
  serverBacked: false,
};

export const getWishlistProductId = (product, activeProductId = null) => {
  if (
    product.class === ProductClass.COLLECTION ||
    product.class === ProductClass.MULTI
  ) {
    return activeProductId || product.id;
  }

  return product.id;
};

const getWishlistProductLink = (
  product,
  routeService,
  activeProductId = null
) => {
  if (
    (product.class === ProductClass.COLLECTION ||
      product.class === ProductClass.MULTI) &&
    activeProductId
  ) {
    return routeService.getCanonicalProductPath(
      product,
      product.pathWithActiveProductId(activeProductId)
    );
  }

  if (product.class === ProductClass.MULTI_CHILD) {
    return routeService.getCanonicalProductPath(
      product,
      product.pathWithActiveProductId(product.id)
    );
  }

  return routeService.getCanonicalProductPath(product);
};

export const stripWishlistItemMeta = (item) => {
  if (!item) {
    return item;
  }

  const { dirty, serverBacked, extendedId, ...runtimeItem } = item;
  return runtimeItem;
};

export const normalizeWishlistItem = (item, overrides = {}) => {
  const normalized = {
    ...stripWishlistItemMeta(item),
    extendedId: String(
      item && (item.extendedId || item.id || item.product_id || '')
    ),
    quantity: Number(item && item.quantity ? item.quantity : 0),
    dirty: item && item.dirty === true,
    serverBacked: item && item.serverBacked === true,
    ...overrides,
  };

  normalized.id = normalized.extendedId;
  normalized.product_id = String(
    normalized.product_id || normalized.extendedId || ''
  );
  normalized.multiproduct_id = normalized.multiproduct_id || '';
  normalized.stock_unit = normalized.stock_unit || '';
  normalized.variations = Array.isArray(normalized.variations)
    ? normalized.variations
    : [];
  normalized.dirty = normalized.dirty === true;
  normalized.serverBacked = normalized.serverBacked === true;

  return normalized;
};

export const normalizeWishlistDocument = (document, customerId = null) => {
  const items = Array.isArray(document && document.items) ? document.items : [];
  const pendingDeletions = Array.isArray(document && document.pendingDeletions)
    ? [...new Set(document.pendingDeletions.map((item) => String(item)))]
    : [];

  return {
    version: WISHLIST_STORAGE_VERSION,
    customerId:
      document && document.customerId !== undefined
        ? document.customerId
        : customerId,
    items: items.map((item) => normalizeWishlistItem(item)),
    pendingDeletions,
    lastHydratedAt:
      document && document.lastHydratedAt ? document.lastHydratedAt : null,
  };
};

export const createEmptyWishlistDocument = (customerId = null) => ({
  version: WISHLIST_STORAGE_VERSION,
  customerId,
  items: [],
  pendingDeletions: [],
  lastHydratedAt: null,
});

export const createWishlistItemFromProduct = (
  product,
  quantity,
  routeService,
  variations,
  activeProductId = null
) => {
  const price = product.price_info ? product.price_info.price : 0.0;
  const extendedId = getWishlistProductId(product, activeProductId);

  return normalizeWishlistItem(
    {
      actual_code: product.productCode,
      availability_html: product.availability_html,
      available_for_purchase: product.can_be_ordered_out_of_stock,
      hierarchy: toPlainValue(product.mainCategory?.hierarchy),
      class: product.class,
      collection: toPlainValue(product.collection),
      extendedId,
      id: extendedId,
      image: getWishlistProductImage(product, activeProductId),
      link: getWishlistProductLink(product, routeService, activeProductId),
      manufacturer: toPlainValue(product.manufacturer),
      main_category_id: product.main_category_id,
      main_section_id: product.main_section_id,
      multi: toPlainValue(product.multi),
      multiproduct_id: product.multiproduct_id,
      multiproduct_title: product.multiproduct_title,
      name: product.name,
      package_size: product.package_size,
      price_info: toPlainValue(product.price_info),
      product_id: product.id,
      quantity: Number(quantity),
      recurring_order_type: product.recurring_order_type,
      stock_unit: product.stock_unit,
      total_price: toPlainValue(price),
      variations,
      ...itemMetaDefaults,
    },
    itemMetaDefaults
  );
};

export const createLegacyWishlistDocument = (legacyItems) => {
  return normalizeWishlistDocument({
    version: WISHLIST_STORAGE_VERSION,
    customerId: null,
    items: Array.isArray(legacyItems)
      ? legacyItems.map((item) =>
          normalizeWishlistItem(item, {
            dirty: true,
            serverBacked: false,
          })
        )
      : [],
    pendingDeletions: [],
    lastHydratedAt: null,
  });
};

export const mergeWishlistItems = (localItems, serverItems) => {
  const serverById = new Map(
    serverItems.map((item) => [String(item.extendedId || item.id), item])
  );

  const mergedItems = localItems.map((item) => {
    const itemId = String(item.extendedId || item.id);
    const serverItem = serverById.get(itemId);

    if (!serverItem) {
      return normalizeWishlistItem(item);
    }

    serverById.delete(itemId);

    return normalizeWishlistItem(
      {
        ...serverItem,
        quantity: item.quantity,
      },
      {
        dirty: item.dirty === true,
        serverBacked: true,
      }
    );
  });

  serverById.forEach((item) => {
    mergedItems.push(
      normalizeWishlistItem(item, {
        dirty: false,
        serverBacked: true,
      })
    );
  });

  return mergedItems;
};

function getSmallProductImageOrDefault(images) {
  return images && images[0] ? images[0].sizes.small : null;
}

function getWishlistProductImage(product, activeProductId) {
  const collectionImage = getCollectionWishlistImage(product, activeProductId);
  if (collectionImage) {
    return collectionImage;
  }

  const mainImage =
    typeof product.getMainImage === 'function'
      ? product.getMainImage(activeProductId)
      : null;

  if (mainImage && mainImage.sizes) {
    return mainImage.sizes.small;
  }

  return product.image || getSmallProductImageOrDefault(product.images);
}

function getCollectionWishlistImage(product, activeProductId) {
  if (
    product.class !== ProductClass.COLLECTION ||
    !activeProductId ||
    !product.collection ||
    typeof product.collection.getItemWithProductId !== 'function' ||
    !Array.isArray(product.images)
  ) {
    return null;
  }

  const collectionItem =
    product.collection.getItemWithProductId(activeProductId);
  if (!collectionItem) {
    return null;
  }

  const matchingImage = product.images.find((image) =>
    [collectionItem.row_id, collectionItem.column_id].includes(
      image.for_color_id
    )
  );

  return matchingImage ? matchingImage.sizes.small : null;
}

function toPlainValue(value) {
  if (isStateTreeNode(value)) {
    return getSnapshot(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => toPlainValue(item));
  }

  return value;
}
